/**
 * useInstanceControl — the parent console's live-instance data layer.
 *
 * Wraps the instance endpoints on /api/chores:
 *   - useLiveInstances(status)  GET  /api/chores/instances?date=&status=
 *   - useReassignInstance()     POST /api/chores/:id/reassign
 *   - useCancelInstance()       POST /api/chores/:id/cancel
 *   - useRestoreInstance()      POST /api/chores/:id/restore
 *
 * The list is requireAdmin (read-only), the three mutations requireAdminScope —
 * so they throw the shared ApiError and the console's <AdminReauthModal> can
 * detect a 403 admin-scope error and retry, exactly as the template library
 * does.
 *
 * Every mutation invalidates the ['chores'] family so the kid-facing board and
 * this list move together; ['approvals'] too, since cancelling something in
 * awaiting_approval removes it from the queue.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../admin/useMembers';
import { osloToday } from '../../lib/osloToday';
import { useSession } from '../auth/useSession';

/** A chore_instances row as returned by GET /api/chores/instances. */
export interface InstanceRow {
  id: string;
  chore_id: string;
  status:
    | 'available'
    | 'claimed'
    | 'awaiting_approval'
    | 'completed'
    | 'rejected'
    | 'expired'
    | 'cancelled';
  assigned_to: string | null;
  claimed_by: string | null;
  claimed_at: string | null;
  claim_expires_at: string | null;
  completed_at: string | null;
  due_at: string | null;
  spawned_at: string;
  title_snapshot: string;
  point_value_snapshot: number;
  tint_snapshot: string | null;
}

/** Filter values the console offers. 'all' omits the query param entirely. */
export type InstanceFilter =
  | 'all'
  | 'available'
  | 'claimed'
  | 'awaiting_approval'
  | 'cancelled'
  | 'expired';

export const LIVE_INSTANCES_KEY = ['admin', 'chore-instances'] as const;

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'unknown';
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === 'string') code = body.error;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, code);
}

/**
 * Dagens kort (#159, steg 9b): `?date=` er dagens Oslo-dato, så både flisene
 * «Oppgaver i dag» og listen de åpner teller bare kort spredd i dag, og uten
 * API-ets tak på 50. Datoen står i nøkkelen, så en ny dag er en ny spørring.
 */
export function useLiveInstances(filter: InstanceFilter = 'all') {
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  const date = osloToday();
  return useQuery<InstanceRow[]>({
    queryKey: [...LIVE_INSTANCES_KEY, date, filter],
    enabled: isAdmin,
    queryFn: async () => {
      const qs = new URLSearchParams({ date });
      if (filter !== 'all') qs.set('status', filter);
      const res = await fetch(`/api/chores/instances?${qs.toString()}`, { credentials: 'same-origin' });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as InstanceRow[];
    },
    // Short window: this is the "what happened today" view, and a parent acting
    // on it wants to see the result immediately.
    staleTime: 10_000,
  });
}

/** Shared invalidation — the console list, the kid board, and the queue. */
function useInstanceInvalidation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: LIVE_INSTANCES_KEY });
    // ['chores'] prefix-matches ['chores','board'].
    void qc.invalidateQueries({ queryKey: ['chores'] });
    void qc.invalidateQueries({ queryKey: ['homework'] });
    void qc.invalidateQueries({ queryKey: ['approvals'] });
  };
}

export function useReassignInstance() {
  const invalidate = useInstanceInvalidation();
  return useMutation({
    mutationFn: async (args: { instanceId: string; memberId: string }) => {
      const res = await fetch(`/api/chores/${args.instanceId}/reassign`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId: args.memberId }),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as InstanceRow;
    },
    onSuccess: invalidate,
  });
}

export function useCancelInstance() {
  const invalidate = useInstanceInvalidation();
  return useMutation({
    mutationFn: async (instanceId: string) => {
      const res = await fetch(`/api/chores/${instanceId}/cancel`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as InstanceRow;
    },
    onSuccess: invalidate,
  });
}

export function useRestoreInstance() {
  const invalidate = useInstanceInvalidation();
  return useMutation({
    mutationFn: async (instanceId: string) => {
      const res = await fetch(`/api/chores/${instanceId}/restore`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as InstanceRow;
    },
    onSuccess: invalidate,
  });
}

/**
 * Manual point adjustment. Separate from the instance hooks but shares their
 * admin-scope error shape; invalidates the wallet + leaderboard surfaces.
 */
export function useAdjustPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { memberId: string; amount: number; note: string }) => {
      const res = await fetch('/api/points/adjust', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as { memberId: string; amount: number; balance: number };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['points'] });
      void qc.invalidateQueries({ queryKey: ['wallet'] });
      void qc.invalidateQueries({ queryKey: ['leaderboard'] });
      void qc.invalidateQueries({ queryKey: ['child-overview'] });
    },
  });
}
